<script setup lang="ts">
import { ref, computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { deletedShiftsApi } from '@/api/shift-versions';
import { usePermissions } from '@/composables/usePermissions';
import { useResponsiveTable } from '@/composables/useResponsiveTable';

const props = defineProps<{
  patientId?: number;
  caregiverId?: number;
  filterName: string;
  filterType: string;
  startDate: string;
  endDate: string;
}>();

const { hasPermission } = usePermissions();
const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 10 });
const dialogOpen = ref(false);
const isFilteredByCaregiver = computed(() => !!props.caregiverId && !props.patientId);

function formatDateLabel(dateStr: string) {
  const datePart = dateStr.split('T')[0] ?? dateStr;
  const [year, month, day] = datePart.split('-');
  return `${day}/${month}/${year}`;
}

const dialogTitle = computed(() => {
  const start = formatDateLabel(props.startDate);
  const end = formatDateLabel(props.endDate);
  return `Turnos Eliminados — ${props.filterType}: ${props.filterName} (${start} - ${end})`;
});

const { data: deletedShifts, isPending } = useQuery({
  queryKey: ['deletedShifts', props.patientId, props.caregiverId, props.startDate, props.endDate],
  queryFn: () => deletedShiftsApi.index({
    patientId: props.patientId,
    caregiverId: props.caregiverId,
    startDate: props.startDate,
    endDate: props.endDate,
  }),
  enabled: computed(() => dialogOpen.value && (!!props.patientId || !!props.caregiverId)),
});

const canSeeFinancial = computed(() => hasPermission.value('shifts', 'read_financial_information'));

const headers = computed(() => {
  const bold = { class: 'font-weight-bold' };
  const base = [
    { title: 'Fecha', value: 'shiftData.humanDate', sortable: true, headerProps: bold },
    { title: 'Horario', value: 'shiftData.humanTime', sortable: false, headerProps: bold },
    isFilteredByCaregiver.value ?
      { title: 'Paciente', value: 'shiftData.patientName', sortable: true, headerProps: bold } :
      { title: 'Profesional', value: 'shiftData.caregiverName', sortable: true, headerProps: bold },
    { title: 'Eliminado Por', value: 'deletedBy', sortable: true, headerProps: bold },
    { title: 'Fecha Eliminación', value: 'deletedAt', sortable: true, headerProps: bold },
  ];

  const financialInsertIndex = 3;
  if (canSeeFinancial.value) {
    base.splice(financialInsertIndex, 0,
      { title: 'Precio', value: 'shiftData.price', sortable: true, headerProps: bold },
      { title: 'Costo', value: 'shiftData.caregiverCost', sortable: true, headerProps: bold },
    );
  }

  return base;
});
</script>
<template>
  <v-dialog
    v-model="dialogOpen"
    max-width="900"
    scrollable
  >
    <template #activator="{ props: activatorProps }">
      <v-btn
        v-bind="activatorProps"
        icon="mdi-delete-clock"
        color="black"
        size="small"
        variant="text"
        class="pb-1 ma-2"
      >
        <v-icon icon="mdi-delete-clock" />
        <v-tooltip
          activator="parent"
          location="top"
        >
          Ver turnos eliminados del período
        </v-tooltip>
      </v-btn>
    </template>
    <template #default="{ isActive }">
      <v-card>
        <v-card-title class="d-flex align-center text-subtitle-1">
          <v-icon class="me-2">
            mdi-delete-clock
          </v-icon>
          {{ dialogTitle }}
          <v-spacer />
          <v-btn
            icon="mdi-close"
            variant="text"
            @click="isActive.value = false"
          />
        </v-card-title>
        <v-divider />
        <v-card-text>
          <div
            v-if="isPending"
            class="d-flex justify-center pa-8"
          >
            <v-progress-circular
              indeterminate
              color="primary"
            />
          </div>
          <div
            v-else-if="!deletedShifts || deletedShifts.length === 0"
            class="text-center text-grey pa-8"
          >
            <v-icon
              size="48"
              class="mb-4"
            >
              mdi-delete-off
            </v-icon>
            <div>No hay turnos eliminados en este período.</div>
          </div>
          <v-data-table
            v-else
            v-bind="tableProps"
            :headers="headers"
            :items="deletedShifts"
            density="comfortable"
          >
            <template #item.deletedBy="{ item }">
              {{ item.deletedBy || '-' }}
            </template>
            <template #item.shiftData.price="{ item }">
              {{
                item.shiftData.price != null
                  ? `$${item.shiftData.price.toLocaleString('es-CL')}`
                  : '-'
              }}
            </template>
            <template #item.shiftData.caregiverCost="{ item }">
              {{
                item.shiftData.caregiverCost != null
                  ? `$${item.shiftData.caregiverCost.toLocaleString('es-CL')}`
                  : '-'
              }}
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            variant="text"
            @click="isActive.value = false"
          >
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </template>
  </v-dialog>
</template>
